import React, { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import { useActiveOrganization } from 'hooks/use-organization'
import { authenticatedUserAtom, toastAtom } from 'state'
import { useRecoilState, useSetRecoilState } from 'recoil'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { getColorForDarkMode, isFieldEmpty } from 'utils'
import {
  GmailQuery,
  OnboardingStep,
  Reference,
  SourceCalendar,
  SourceDocument,
  SourceType,
  TwinInstruction,
  TwinInstructionAccess,
  TwinNote,
  TwinNoteSummary,
} from 'api/model'
import { useFetchSourceDocuments } from 'hooks/use-source-documents'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useFetchNotes } from 'hooks/use-notes'
import useDrivePicker from 'react-google-drive-picker'
import { API_KEY, CLIENT_ID } from './MyTwinFiles'
import {
  sourcesNewDocument,
  twinsNewInstruction,
  twinsUpdateInstruction,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import AddOrEditNoteModal from './AddOrEditNoteModal'
import './AddInstructionModal.scss'
import { useFetchGmailQueries, useFetchSourceCalendars, useFetchSources } from 'hooks/use-sources'
import AddEmailQueryModal from './AddEmailQueryModal'
import { formatGoogleDrivePickerDocuments } from './utils'
import styled from '@emotion/styled'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { getElementColor } from 'utils/darkMode'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import InstructionAccessRadioGroup from './InstructionAccessRadioGroup'
import ReferencesList from 'components/References'
import AttachDocumentSelector from 'components/Gmail/AttachDocumentSelector'
import AttachedDocumentsList from 'components/Gmail/AttachedDocumentsList'
import { DocumentType } from 'components/Gmail/AttachDocumentSelector'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { COLORS } from 'app-constants'

const CheckboxContainerStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`

export const StyledCheckboxWithDarkMode = styled(Checkbox)<{ theme: Theme }>`
  margin-right: 8px;
  padding: 0;
`

export const AddDocumentDialog = ({
  isAddDocumentDialogOpen,
  setIsAddDocumentDialogOpen,
  onAddDocument,
  existingDocuments,
}: {
  isAddDocumentDialogOpen: boolean
  setIsAddDocumentDialogOpen: (isOpen: boolean) => void
  onAddDocument: (sourceDocuments: SourceDocument[]) => void
  existingDocuments: SourceDocument[]
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()

  const [selectedDocuments, setSelectedDocuments] = useState<SourceDocument[]>([])

  useEffect(() => {
    setSelectedDocuments(existingDocuments)
  }, [existingDocuments])

  const { sourceDocuments, isLoading: isLoadingSourceDocuments, setSourceDocuments } = useFetchSourceDocuments()

  const [openPicker] = useDrivePicker()

  const handleOpenPicker = () => {
    openPicker({
      clientId: CLIENT_ID ?? '',
      developerKey: API_KEY ?? '',
      viewId: 'DOCS',
      // token: user?.google_oauth_token ?? '',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      customScopes: ['https://www.googleapis.com/auth/drive.readonly'],
      // customViews: customViewsArray, // custom view
      callbackFunction: async (data) => {
        if (!activeOrganization) {
          return
        }
        if (data.docs) {
          const formattedDocs = formatGoogleDrivePickerDocuments(data.docs)

          // do not add duplicate existing documents
          const filteredDocs = formattedDocs.filter((doc) => {
            return !sourceDocuments.find((d) => d.document_id === doc.document_id)
          })
          const hasDuplicateDocuments = formattedDocs.length !== filteredDocs.length

          try {
            const response = await sourcesNewDocument(activeOrganization, formattedDocs)

            // should only add documents that were not already in the list
            const filteredResponse = response.data.filter((doc) => {
              return !sourceDocuments.find((d) => d.document_id === doc.document_id)
            })
            setSourceDocuments([...sourceDocuments, ...filteredResponse])
            setSelectedDocuments([...selectedDocuments, ...filteredResponse])

            if (hasDuplicateDocuments) {
              setToast({
                title: 'Successfully updated documents',
                severity: 'success',
              })
            } else {
              setToast({
                title: 'Successfully added documents',
                severity: 'success',
              })
            }
          } catch {
            setToast({
              title: 'Failed to add documents',
              severity: 'error',
            })
          }
        }
      },
    })
  }

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredSourceDocuments = sourceDocuments.filter((sd) => {
    return sd.name.toLowerCase().includes(searchText.toLowerCase()) || selectedDocuments.find((s) => s.id === sd.id)
  })

  // TODO: add loading state
  if (isLoadingSourceDocuments) {
    return null
  }

  return (
    <Dialog open={isAddDocumentDialogOpen}>
      <DialogTitle
        style={{
          marginBottom: '16px',
        }}>
        Which documents would you like to add?
      </DialogTitle>

      <DialogContent>
        <Stack>
          <TextField
            placeholder="Search for document"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            style={{
              marginBottom: '16px',
              width: isMobile ? '100%' : '360px',
            }}
          />
          {filteredSourceDocuments.length === 0 && (
            <Typography variant="pS" color="secondary">
              No documents found
            </Typography>
          )}
          <Stack spacing={1}>
            {filteredSourceDocuments.map((f) => {
              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  key={f.id}
                  onClick={() => {
                    const selectedSourceDocument = selectedDocuments.find((s) => s.id === f.id)
                    if (selectedSourceDocument) {
                      setSelectedDocuments(selectedDocuments.filter((t) => t.id !== f.id))
                    } else {
                      setSelectedDocuments([...selectedDocuments, f])
                    }
                  }}
                  sx={{
                    border: selectedDocuments.find((s) => s.id === f.id) ? '2px solid #222222' : 'none',
                    borderRadius: '4px',
                    py: '8px',
                    px: '16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: COLORS.GRAY_8,
                    },
                  }}>
                  <img
                    src={f.icon_url}
                    alt="icon"
                    style={{
                      width: '24px',
                      height: '24px',
                      marginRight: '8px',
                    }}
                  />

                  <Typography variant="h6" ml={1} color="secondary">
                    {f.name}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: isMobile ? 'column' : 'row',
        }}>
        <Button
          color="secondary"
          onClick={() => {
            setIsAddDocumentDialogOpen(false)
          }}>
          Cancel
        </Button>
        <Button
          size={isMobile ? 'small' : 'medium'}
          variant="contained"
          color="secondary"
          onClick={() => {
            handleOpenPicker()
          }}>
          Add new document(s)
        </Button>
        <Button
          disabled={selectedDocuments.length === 0}
          size={isMobile ? 'small' : 'medium'}
          variant="contained"
          color="primary"
          onClick={() => {
            onAddDocument(selectedDocuments)
            setIsAddDocumentDialogOpen(false)
          }}>
          Select document(s)
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AddCalendarDataSourceDialog = ({
  isAddCalendarDialogOpen,
  setIsAddCalendarDialogOpen,
  onAddCalendarDataSource,
}: {
  isAddCalendarDialogOpen: boolean
  setIsAddCalendarDialogOpen: (isOpen: boolean) => void
  onAddCalendarDataSource: (calendars: SourceCalendar[]) => void
}) => {
  const isMobile = useCheckMobileScreen()

  const { isLoading, sourceCalendars } = useFetchSourceCalendars()

  const [selectedCalendars, setSelectedCalendars] = useState<SourceCalendar[]>([])

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredCalendars = sourceCalendars.filter((sd) => {
    return sd.name?.toLowerCase().includes(searchText.toLowerCase()) || selectedCalendars.find((s) => s.id === sd.id)
  })

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  return (
    <Dialog open={isAddCalendarDialogOpen}>
      <DialogTitle
        style={{
          marginBottom: '16px',
        }}>
        Which calendars would you like to add as a data source?
      </DialogTitle>

      <DialogContent>
        <Stack>
          <TextField
            placeholder="Search for calendar"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            style={{
              marginBottom: '16px',
              width: isMobile ? '100%' : '360px',
            }}
          />
          {filteredCalendars.map((f) => {
            return (
              <Stack
                key={f.id}
                direction="row"
                alignItems="center"
                onClick={() => {
                  const selectedCalendar = selectedCalendars.find((s) => s.id === f.id)
                  if (selectedCalendar) {
                    setSelectedCalendars(selectedCalendars.filter((s) => s.id !== f.id))
                  } else {
                    setSelectedCalendars([...selectedCalendars, f])
                  }
                }}
                sx={{
                  border: selectedCalendars.find((s) => s.id === f.id) ? '2px solid #222222' : 'none',
                  borderRadius: '4px',
                  py: '8px',
                  px: '16px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: COLORS.GRAY_8,
                  },
                }}>
                <Typography variant="h6" ml={1} color="secondary">
                  {f.name}
                </Typography>
                <Typography variant="pS" ml={1} color="secondary">
                  {f.description}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: isMobile ? 'column' : 'row',
        }}>
        <Button
          color="secondary"
          onClick={() => {
            setIsAddCalendarDialogOpen(false)
          }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedCalendars.length > 0) {
              onAddCalendarDataSource(selectedCalendars)
              setIsAddCalendarDialogOpen(false)
            }
          }}>
          Select calendar(s)
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AddEmailQueryDataSourceDialog = ({
  isAddEmailQueryDialogOpen,
  setIsAddEmailQueryDialogOpen,
  onAddEmailQueryDataSource,
}: {
  isAddEmailQueryDialogOpen: boolean
  setIsAddEmailQueryDialogOpen: (isOpen: boolean) => void
  onAddEmailQueryDataSource: (emailQueries: GmailQuery[]) => void
}) => {
  const isMobile = useCheckMobileScreen()

  const { isLoading: isLoadingSources, sources } = useFetchSources()

  const gmailSource = sources.find((s) => s.type === SourceType.gmail)
  const {
    gmailQueries,
    isLoading: isLoadingGmailQueries,
    setGmailQueries,
  } = useFetchGmailQueries(gmailSource?.id || '')

  const [selectedEmailQueries, setSelectedEmailQueries] = useState<GmailQuery[]>([])

  const [isAddEmailQueryModalOpen, setIsAddEmailQueryModalOpen] = useState<boolean>(false)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredEmailQueries = gmailQueries.filter((gq) => {
    return gq.query.toLowerCase().includes(searchText.toLowerCase()) || selectedEmailQueries.find((s) => s.id === gq.id)
  })

  const onEmailIndexAdded = (emailQuery: GmailQuery) => {
    setGmailQueries([...gmailQueries, emailQuery])
    setSelectedEmailQueries([...selectedEmailQueries, emailQuery])
  }

  // TODO: add loading state
  if (isLoadingSources || isLoadingGmailQueries) {
    return null
  }

  return (
    <Dialog open={isAddEmailQueryDialogOpen}>
      <DialogTitle
        style={{
          marginBottom: '16px',
        }}>
        Which email queries would you like to add as a data source?
      </DialogTitle>

      <DialogContent>
        <Stack>
          <TextField
            placeholder="Search for email query"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            style={{
              marginBottom: '16px',
              width: isMobile ? '100%' : '360px',
            }}
          />
          <Stack spacing={1}>
            {filteredEmailQueries.map((f) => {
              return (
                <Stack
                  key={f.id}
                  direction="row"
                  alignItems="center"
                  onClick={() => {
                    const selectedEmailQuery = selectedEmailQueries.find((s) => s.id === f.id)
                    if (selectedEmailQuery) {
                      setSelectedEmailQueries(selectedEmailQueries.filter((s) => s.id !== f.id))
                    } else {
                      setSelectedEmailQueries([...selectedEmailQueries, f])
                    }
                  }}
                  sx={{
                    border: selectedEmailQueries.find((s) => s.id === f.id) ? '2px solid #222222' : 'none',
                    borderRadius: '4px',
                    py: '8px',
                    px: '16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: COLORS.GRAY_8,
                    },
                  }}>
                  <Typography variant="h6" ml={1} color="secondary">
                    {f.query}
                  </Typography>
                  <Typography variant="pS" ml={1} color="secondary">
                    {f.date_updated}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: isMobile ? 'column' : 'row',
        }}>
        <Button
          color="secondary"
          onClick={() => {
            setIsAddEmailQueryDialogOpen(false)
          }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setIsAddEmailQueryModalOpen(true)
          }}>
          Add new email query
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedEmailQueries.length > 0) {
              onAddEmailQueryDataSource(selectedEmailQueries)
              setIsAddEmailQueryDialogOpen(false)
            }
          }}>
          Select email queries
        </Button>
      </DialogActions>
      {isAddEmailQueryModalOpen && (
        <AddEmailQueryModal
          gmailQueries={gmailQueries}
          onEmailIndexAdded={onEmailIndexAdded}
          sourceId={gmailSource?.id ?? ''}
          onClose={() => setIsAddEmailQueryModalOpen(false)}
        />
      )}
    </Dialog>
  )
}

export const AddNoteDialog = ({
  isAddNoteDialogOpen,
  setIsAddNoteDialogOpen,
  onAddNote,
  existingNotes,
}: {
  isAddNoteDialogOpen: boolean
  setIsAddNoteDialogOpen: (isOpen: boolean) => void
  onAddNote: (notes: TwinNoteSummary[]) => void
  existingNotes: TwinNoteSummary[]
}) => {
  const isMobile = useCheckMobileScreen()

  const { notes, isLoading: isLoadingNotes, setNotes } = useFetchNotes()

  const [selectedNotes, setSelectedNotes] = useState<TwinNoteSummary[]>([])

  useEffect(() => {
    setSelectedNotes(existingNotes)
  }, [existingNotes])

  const [isAddOrEditNoteModalOpen, setIsAddOrEditNoteModalOpen] = useState<boolean>(false)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredNotes = notes.filter((note) => {
    return note.title.toLowerCase().includes(searchText.toLowerCase()) || selectedNotes.find((s) => s.id === note.id)
  })

  // hydrate notes on client side
  const onHydrateNotes = (note: TwinNote) => {
    const updatedNote: TwinNoteSummary = {
      id: note.id,
      title: note.title,
      twin_id: note.twin_id,
      summary: note.content.substring(0, 30), // we need this because the API doesn't return the summary
      date_updated: note.date_updated,
      date_created: note.date_created,
      search_scope: note.search_scope,
    }

    setNotes((prevNotes) => [updatedNote, ...prevNotes])
    setSelectedNotes((prevNotes) => [...prevNotes, updatedNote])
  }

  // TODO: if loading notes
  if (isLoadingNotes) {
    return null
  }

  return (
    <Dialog open={isAddNoteDialogOpen}>
      <DialogTitle
        style={{
          marginBottom: '16px',
        }}>
        Which notes would you like to add?
      </DialogTitle>

      <DialogContent>
        <Stack>
          <TextField
            placeholder="Search for note"
            value={searchText}
            onChange={onSearchTextChange}
            fullWidth
            style={{
              marginBottom: '16px',
              width: isMobile ? '100%' : '360px',
            }}
          />
          <Stack spacing={1}>
            {filteredNotes.map((f) => {
              return (
                <Stack
                  key={f.id}
                  direction="row"
                  alignItems="center"
                  onClick={() => {
                    const selectedNote = selectedNotes.find((s) => s.id === f.id)
                    if (selectedNote) {
                      setSelectedNotes(selectedNotes.filter((s) => s.id !== f.id))
                    } else {
                      setSelectedNotes([...selectedNotes, f])
                    }
                  }}
                  sx={{
                    border: selectedNotes.find((s) => s.id === f.id) ? '2px solid #222222' : 'none',
                    borderRadius: '4px',
                    py: '8px',
                    px: '16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: COLORS.GRAY_8,
                    },
                  }}>
                  <Typography variant="h6" ml={1} color="secondary">
                    {f.title}
                  </Typography>
                  <Typography variant="pS" ml={1} color="secondary">
                    {f.summary}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: isMobile ? 'column' : 'row',
        }}>
        <Button
          color="secondary"
          onClick={() => {
            setIsAddNoteDialogOpen(false)
          }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setIsAddOrEditNoteModalOpen(true)
          }}>
          Add new note
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedNotes.length > 0) {
              onAddNote(selectedNotes)
              setIsAddNoteDialogOpen(false)
            }
          }}>
          Select note(s)
        </Button>
      </DialogActions>

      {isAddOrEditNoteModalOpen && (
        <AddOrEditNoteModal
          noteId={''}
          onClose={() => setIsAddOrEditNoteModalOpen(false)}
          onAddNote={onHydrateNotes}
          onEditNote={() => {}}
          onHydrateFullNote={() => {}}
        />
      )}
    </Dialog>
  )
}

const MobileInclusionRow = ({ title, onDeleteDataSource }: { title: string; onDeleteDataSource: () => void }) => {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="pS" color="secondary">
        {title}
      </Typography>

      <IconButton onClick={onDeleteDataSource}>
        <DeleteOutlinedIcon />
      </IconButton>
    </Stack>
  )
}

const WebInclusionRow = ({ title, onDeleteDataSource }: { title: string; onDeleteDataSource: () => void }) => {
  return (
    <Stack direction="row" alignItems="center">
      <Typography variant="pS" mr={1} flex="0 0 212px" color="secondary">
        {title}
      </Typography>

      <IconButton onClick={onDeleteDataSource}>
        <DeleteOutlinedIcon />
      </IconButton>
    </Stack>
  )
}

// used to select notes for inclusions / exclusions
const SelectNoteButton = ({
  title,
  notes,
  setNotes,
  submitCount,
}: {
  title: string // Included Notes / Excluded Notes
  notes: TwinNoteSummary[]
  setNotes: (notes: TwinNoteSummary[]) => void
  submitCount: number
}) => {
  const isMobile = useCheckMobileScreen()

  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState<boolean>(false)

  return (
    <Stack>
      <Button
        color="primary"
        size={isMobile ? 'small' : 'medium'}
        onClick={() => setIsAddNoteDialogOpen(true)}
        sx={{
          width: 'fit-content',
          marginBottom: '16px',
        }}>
        Choose note
      </Button>
      <Stack spacing={1} mb={1}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {notes.length > 0 ? (
          notes.map((note, index) => {
            if (isMobile) {
              return (
                <MobileInclusionRow
                  key={index}
                  title={note.title}
                  onDeleteDataSource={() => {
                    const updatedNotes = notes.filter((n) => n.id !== note.id)
                    setNotes(updatedNotes)
                  }}
                />
              )
            }
            return (
              <WebInclusionRow
                key={index}
                title={note.title}
                onDeleteDataSource={() => {
                  const updatedNotes = notes.filter((n: TwinNoteSummary) => n.id !== note.id)
                  setNotes(updatedNotes)
                }}
              />
            )
          })
        ) : (
          <Typography variant="pS" color="secondary">
            No notes selected
          </Typography>
        )}
        {notes.length === 0 && submitCount > 0 && (
          <Typography variant="pXS" color="#FC4131">
            Please select at least one note.
          </Typography>
        )}
      </Stack>
      <AddNoteDialog
        isAddNoteDialogOpen={isAddNoteDialogOpen}
        setIsAddNoteDialogOpen={setIsAddNoteDialogOpen}
        onAddNote={(notes: TwinNoteSummary[]) => {
          setNotes(notes)
        }}
        existingNotes={notes}
      />
    </Stack>
  )
}

const SelectDocumentButton = ({
  title,
  sourceDocuments,
  setSourceDocuments,
  submitCount,
}: {
  title: string
  sourceDocuments: SourceDocument[]
  setSourceDocuments: (sourceDocuments: SourceDocument[]) => void
  submitCount: number
}) => {
  const isMobile = useCheckMobileScreen()
  const [isAddDocumentDialogOpen, setIsAddDocumentDialogOpen] = useState<boolean>(false)

  return (
    <Stack>
      <Button
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        onClick={() => setIsAddDocumentDialogOpen(true)}
        style={{
          width: 'fit-content',
          marginBottom: '16px',
        }}>
        Choose file
      </Button>
      <Stack spacing={1} mb={1}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {sourceDocuments.length > 0 ? (
          sourceDocuments.map((document, index) => {
            if (isMobile) {
              return (
                <MobileInclusionRow
                  key={index}
                  title={document.name}
                  onDeleteDataSource={() => {
                    const updatedDocuments = sourceDocuments.filter((n: SourceDocument) => n.id !== document.id)
                    setSourceDocuments(updatedDocuments)
                  }}
                />
              )
            }
            return (
              <WebInclusionRow
                key={index}
                title={document.name}
                onDeleteDataSource={() => {
                  const updatedDocuments = sourceDocuments.filter((n) => n.id !== document.id)
                  setSourceDocuments(updatedDocuments)
                }}
              />
            )
          })
        ) : (
          <Typography variant="pS" color="secondary">
            No documents selected
          </Typography>
        )}
        {sourceDocuments.length === 0 && submitCount > 0 && (
          <Typography variant="pXS" color="#FC4131">
            Please select at least one document.
          </Typography>
        )}
      </Stack>
      <AddDocumentDialog
        isAddDocumentDialogOpen={isAddDocumentDialogOpen}
        setIsAddDocumentDialogOpen={setIsAddDocumentDialogOpen}
        onAddDocument={(sourceDocuments: SourceDocument[]) => {
          setSourceDocuments(sourceDocuments)
        }}
        existingDocuments={sourceDocuments}
      />
    </Stack>
  )
}

const SelectCalendarButton = ({
  title,
  sourceCalendars,
  setSourceCalendars,
  submitCount,
}: {
  title: string
  sourceCalendars: SourceCalendar[]
  setSourceCalendars: (sourceCalendars: SourceCalendar[]) => void
  submitCount: number
}) => {
  const isMobile = useCheckMobileScreen()
  const [isAddCalendarDialogOpen, setIsAddCalendarDialogOpen] = useState<boolean>(false)

  return (
    <Stack>
      <Button
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        onClick={() => setIsAddCalendarDialogOpen(true)}
        style={{
          width: 'fit-content',
          marginBottom: '16px',
        }}>
        Select calendar data source
      </Button>
      <Stack spacing={1} mb={1}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {sourceCalendars.length > 0 ? (
          sourceCalendars.map((calendar, index) => {
            if (isMobile) {
              return (
                <MobileInclusionRow
                  key={index}
                  title={calendar.name ?? ''}
                  onDeleteDataSource={() => {
                    const updatedCalendars = sourceCalendars.filter((n: SourceCalendar) => n.id !== calendar.id)
                    setSourceCalendars(updatedCalendars)
                  }}
                />
              )
            }
            return (
              <WebInclusionRow
                key={index}
                title={calendar.name ?? ''}
                onDeleteDataSource={() => {
                  const updatedCalendars = sourceCalendars.filter((n) => n.id !== calendar.id)
                  setSourceCalendars(updatedCalendars)
                }}
              />
            )
          })
        ) : (
          <Typography variant="pS" color="secondary">
            No calendars selected
          </Typography>
        )}
        {sourceCalendars.length === 0 && submitCount > 0 && (
          <Typography variant="pXS" color="#FC4131">
            Please select at least one calendar.
          </Typography>
        )}
      </Stack>
      <AddCalendarDataSourceDialog
        isAddCalendarDialogOpen={isAddCalendarDialogOpen}
        setIsAddCalendarDialogOpen={setIsAddCalendarDialogOpen}
        onAddCalendarDataSource={(sourceCalendars: SourceCalendar[]) => {
          setSourceCalendars(sourceCalendars)
        }}
      />
    </Stack>
  )
}

const SelectEmailQueryButton = ({
  title,
  gmailQueries,
  setGmailQueries,
  submitCount,
}: {
  title: string
  gmailQueries: GmailQuery[]
  setGmailQueries: (gmailQueries: GmailQuery[]) => void
  submitCount: number
}) => {
  const isMobile = useCheckMobileScreen()
  const [isAddEmailQueryDialogOpen, setIsAddEmailQueryDialogOpen] = useState<boolean>(false)

  return (
    <Stack>
      <Button
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        onClick={() => setIsAddEmailQueryDialogOpen(true)}
        style={{
          width: 'fit-content',
          marginBottom: '16px',
        }}>
        Select email query data source
      </Button>
      <Stack spacing={1} mb={1}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {gmailQueries.length > 0 ? (
          gmailQueries.map((gmailQuery, index) => {
            if (isMobile) {
              return (
                <MobileInclusionRow
                  key={index}
                  title={gmailQuery.query}
                  onDeleteDataSource={() => {
                    const updatedEmailQueries = gmailQueries.filter((n: GmailQuery) => n.id !== gmailQuery.id)
                    setGmailQueries(updatedEmailQueries)
                  }}
                />
              )
            }
            return (
              <WebInclusionRow
                key={index}
                title={gmailQuery.query}
                onDeleteDataSource={() => {
                  const updatedEmailQueries = gmailQueries.filter((n) => n.id !== gmailQuery.id)
                  setGmailQueries(updatedEmailQueries)
                }}
              />
            )
          })
        ) : (
          <Typography variant="pS" color="secondary">
            No email queries selected
          </Typography>
        )}
      </Stack>
      {gmailQueries.length === 0 && submitCount > 0 && (
        <Typography variant="pXS" color="#FC4131">
          Please select at least one gmail query.
        </Typography>
      )}
      <AddEmailQueryDataSourceDialog
        isAddEmailQueryDialogOpen={isAddEmailQueryDialogOpen}
        setIsAddEmailQueryDialogOpen={setIsAddEmailQueryDialogOpen}
        onAddEmailQueryDataSource={(emailQueries: GmailQuery[]) => {
          setGmailQueries(emailQueries)
        }}
      />
    </Stack>
  )
}

const AddOrEditInstructionModal = ({
  twinId,
  onClose,
  instruction,
  onAddInstruction,
  onUpdateInstruction,
  updateOnboardingStep,
}: {
  twinId: string
  onClose: () => void
  instruction: TwinInstruction | null
  onAddInstruction: (instruction: TwinInstruction) => void
  onUpdateInstruction: (instruction: TwinInstruction) => void
  updateOnboardingStep: () => void
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [user] = useRecoilState(authenticatedUserAtom)
  const hasSetTwinProfile = !!user?.onboarding_steps_completed?.includes(OnboardingStep.set_twin_profile)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [queryTopic, setQueryTopic] = useState<string>(instruction?.query ?? '')
  const onQueryTopicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQueryTopic(event.target.value)
  }

  const [instructionText, setInstructionText] = useState<string>(instruction?.instructions ?? '')
  const onInstructionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInstructionText(event.target.value)
  }

  const [instructionAccess, setInstructionAccess] = useState<TwinInstructionAccess>(
    instruction?.access || TwinInstructionAccess.everyone
  )
  const onInstructionAccessChange = (instructionAccess: TwinInstructionAccess) => {
    setInstructionAccess(instructionAccess)
  }

  // references state
  const [references, setReferences] = useState<Reference[]>(instruction?.references ?? [])

  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState<boolean>(false)

  // inclusions form state
  const [areAllNotesIncluded, setAreAllNotesIncluded] = useState<boolean>(false)
  const [includedNotes, setIncludedNotes] = useState<TwinNoteSummary[]>([])
  const [areSpecificNotesIncluded, setAreSpecificNotesIncluded] = useState<boolean>(false)
  const [areAllDocumentsIncluded, setAreAllDocumentsIncluded] = useState<boolean>(false)
  const [includedDocuments, setIncludedDocuments] = useState<SourceDocument[]>([])
  const [areSpecificDocumentsIncluded, setAreSpecificDocumentsIncluded] = useState<boolean>(false)
  const [areAllCalendarsIncluded, setAreAllCalendarsIncluded] = useState<boolean>(false)
  const [includedCalendars, setIncludedCalendars] = useState<SourceCalendar[]>([])
  const [areSpecificCalendarsIncluded, setAreSpecificCalendarsIncluded] = useState<boolean>(false)
  const [areAllEmailsIncluded, setAreAllEmailsIncluded] = useState<boolean>(false)
  const [includedEmailQueries, setIncludedEmailQueries] = useState<GmailQuery[]>([])
  const [areSpecificEmailsIncluded, setAreSpecificEmailsIncluded] = useState<boolean>(false)

  // exclusions form state
  const [areAllNotesExcluded, setAreAllNotesExcluded] = useState<boolean>(false)
  const [excludedNotes, setExcludedNotes] = useState<TwinNoteSummary[]>([])
  const [areSpecificNotesExcluded, setAreSpecificNotesExcluded] = useState<boolean>(false)
  const [areAllDocumentsExcluded, setAreAllDocumentsExcluded] = useState<boolean>(false)
  const [excludedDocuments, setExcludedDocuments] = useState<SourceDocument[]>([])
  const [areSpecificDocumentsExcluded, setAreSpecificDocumentsExcluded] = useState<boolean>(false)
  const [areAllCalendarsExcluded, setAreAllCalendarsExcluded] = useState<boolean>(false)
  const [excludedCalendars, setExcludedCalendars] = useState<SourceCalendar[]>([])
  const [areSpecificCalendarsExcluded, setAreSpecificCalendarsExcluded] = useState<boolean>(false)
  const [areAllEmailsExcluded, setAreAllEmailsExcluded] = useState<boolean>(false)
  const [excludedEmailQueries, setExcludedEmailQueries] = useState<GmailQuery[]>([])
  const [areSpecificEmailsExcluded, setAreSpecificEmailsExcluded] = useState<boolean>(false)

  // local state keeping track of the documents that we're using to create the instruction with
  const [attachDocumentType, setAttachDocumentType] = useState<DocumentType | null>(null)

  useEffect(() => {
    if (!instruction) {
      return
    }

    setAreAllNotesIncluded(instruction?.inclusions.notes_all ?? false)
    setIncludedNotes(instruction?.inclusions.notes ?? [])
    setAreSpecificNotesIncluded(instruction?.inclusions?.notes?.length > 0)
    setAreAllDocumentsIncluded(instruction?.inclusions.documents_all ?? false)
    setIncludedDocuments(instruction?.inclusions.documents ?? [])
    setAreSpecificDocumentsIncluded(instruction?.inclusions.documents?.length > 0)
    setAreAllCalendarsIncluded(instruction?.inclusions.calendars_all ?? false)
    setIncludedCalendars(instruction?.inclusions.calendars ?? [])
    setAreSpecificCalendarsIncluded(instruction?.inclusions.calendars?.length > 0)
    setAreAllEmailsIncluded(instruction?.inclusions.gmail_all ?? false)
    setIncludedEmailQueries(instruction?.inclusions.gmail_queries ?? [])
    setAreSpecificEmailsIncluded(instruction?.inclusions.gmail_queries?.length > 0)
    setAreAllNotesExcluded(instruction?.exclusions.notes_all ?? false)
    setExcludedNotes(instruction?.exclusions.notes ?? [])
    setAreSpecificNotesExcluded(instruction?.exclusions.notes?.length > 0)
    setAreAllDocumentsExcluded(instruction?.exclusions.documents_all ?? false)
    setExcludedDocuments(instruction?.exclusions.documents ?? [])
    setAreSpecificDocumentsExcluded(instruction?.exclusions.documents?.length > 0)
    setAreAllCalendarsExcluded(instruction?.exclusions.calendars_all ?? false)
    setExcludedCalendars(instruction?.exclusions.calendars ?? [])
    setAreSpecificCalendarsExcluded(instruction?.exclusions.calendars?.length > 0)
    setAreAllEmailsExcluded(instruction?.exclusions.gmail_all ?? false)
    setExcludedEmailQueries(instruction?.exclusions.gmail_queries ?? [])
    setAreSpecificEmailsExcluded(instruction?.exclusions.gmail_queries?.length > 0)
  }, [instruction])

  // we should set the submit count to be 0 whenever we make changes to included data sources
  useEffect(() => {
    setSubmitCount(0)
  }, [includedDocuments, includedCalendars, includedEmailQueries, includedNotes])

  // whenever included documents changes, we should set areSpecificDocumentsIncluded to true
  useEffect(() => {
    if (includedDocuments.length > 0 && !areSpecificDocumentsIncluded) {
      setAreSpecificDocumentsIncluded(true)
    }
  }, [includedDocuments, areSpecificDocumentsIncluded])

  // whenever included notes changes, we should set areSpecificNotesIncluded to true
  useEffect(() => {
    if (includedNotes.length > 0 && !areSpecificNotesIncluded) {
      setAreSpecificNotesIncluded(true)
    }
  }, [includedNotes, areSpecificNotesIncluded])

  const areAnyInclusionsSelected = () => {
    return (
      areAllNotesIncluded ||
      areSpecificNotesIncluded ||
      areAllDocumentsIncluded ||
      areSpecificDocumentsIncluded ||
      areAllCalendarsIncluded ||
      areSpecificCalendarsIncluded ||
      areAllEmailsIncluded ||
      areSpecificEmailsIncluded
    )
  }

  const areAnyExclusionsSelected = () => {
    return (
      areAllNotesExcluded ||
      areSpecificNotesExcluded ||
      areAllDocumentsExcluded ||
      areSpecificDocumentsExcluded ||
      areAllCalendarsExcluded ||
      areSpecificCalendarsExcluded ||
      areAllEmailsExcluded ||
      areSpecificEmailsExcluded
    )
  }

  const onFormSubmit = async () => {
    setIsSubmitting(true)
    setSubmitCount((submitCount) => submitCount + 1)

    const isMissingSelectedNoteInclusions = areSpecificNotesIncluded && includedNotes.length === 0
    const isMissingSelectedDocumentInclusions = areSpecificDocumentsIncluded && includedDocuments.length === 0
    const isMissingSelectedCalendarInclusions = areSpecificCalendarsIncluded && includedCalendars.length === 0
    const isMissingSelectedEmailInclusions = areSpecificEmailsIncluded && includedEmailQueries.length === 0

    const isMissingSelectedNoteExclusions = areSpecificNotesExcluded && excludedNotes.length === 0
    const isMissingSelectedDocumentExclusions = areSpecificDocumentsExcluded && excludedDocuments.length === 0
    const isMissingSelectedCalendarExclusions = areSpecificCalendarsExcluded && excludedCalendars.length === 0
    const isMissingSelectedEmailExclusions = areSpecificEmailsExcluded && excludedEmailQueries.length === 0

    if (
      !activeOrganization ||
      !twinId ||
      !isInstructionInputValid() ||
      isMissingSelectedCalendarExclusions ||
      isMissingSelectedCalendarInclusions ||
      isMissingSelectedDocumentExclusions ||
      isMissingSelectedDocumentInclusions ||
      isMissingSelectedEmailExclusions ||
      isMissingSelectedEmailInclusions ||
      isMissingSelectedNoteExclusions ||
      isMissingSelectedNoteInclusions
    ) {
      setIsSubmitting(false)
      return
    }

    const getInclusions = () => {
      if (
        !areAllNotesIncluded &&
        !areSpecificNotesIncluded &&
        !areAllDocumentsIncluded &&
        !areSpecificDocumentsIncluded &&
        !areAllCalendarsIncluded &&
        !areSpecificCalendarsIncluded &&
        !areAllEmailsIncluded &&
        !areSpecificEmailsIncluded
      ) {
        return {
          notes_all: false,
          note_ids: [],
          documents_all: false,
          document_ids: [],
          calendars_all: false,
          calendar_ids: [],
          gmail_all: false,
          gmail_query_ids: [],
        }
      }
      return {
        notes_all: areAllNotesIncluded,
        note_ids: areSpecificNotesIncluded ? includedNotes.map((n) => n.id) : [],
        documents_all: areAllDocumentsIncluded,
        document_ids: areSpecificDocumentsIncluded ? includedDocuments.map((d) => d.id) : [],
        calendars_all: areAllCalendarsIncluded,
        calendar_ids: areSpecificCalendarsIncluded ? includedCalendars.map((c) => c.id) : [],
        gmail_all: areAllEmailsIncluded,
        gmail_query_ids: areSpecificEmailsIncluded ? includedEmailQueries.map((e) => e.id) : [],
      }
    }

    const getExclusions = () => {
      if (
        !areAllNotesExcluded &&
        !areSpecificNotesExcluded &&
        !areAllDocumentsExcluded &&
        !areSpecificDocumentsExcluded &&
        !areAllCalendarsExcluded &&
        !areSpecificCalendarsExcluded &&
        !areAllEmailsExcluded &&
        !areSpecificEmailsExcluded
      ) {
        return {
          notes_all: false,
          note_ids: [],
          documents_all: false,
          document_ids: [],
          calendars_all: false,
          calendar_ids: [],
          gmail_all: false,
          gmail_query_ids: [],
        }
      }
      return {
        notes_all: areAllNotesExcluded,
        note_ids: areSpecificNotesExcluded ? excludedNotes.map((n) => n.id) : [],
        documents_all: areAllDocumentsExcluded,
        document_ids: areSpecificDocumentsExcluded ? excludedDocuments.map((d) => d.id) : [],
        calendars_all: areAllCalendarsExcluded,
        calendar_ids: areSpecificCalendarsExcluded ? excludedCalendars.map((c) => c.id) : [],
        gmail_all: areAllEmailsExcluded,
        gmail_query_ids: areSpecificEmailsExcluded ? excludedEmailQueries.map((e) => e.id) : [],
      }
    }

    // edit case
    if (instruction) {
      try {
        const response = await twinsUpdateInstruction(activeOrganization, twinId, instruction.id, {
          query: queryTopic,
          instructions: instructionText,
          access: instructionAccess,
          references: references.length > 0 ? references : undefined,
          inclusions: getInclusions(),
          exclusions: getExclusions(),
        })

        onUpdateInstruction(response.data)

        setToast({
          title: 'Successfully updated instruction',
          severity: 'success',
        })
        onClose()
      } catch (Error) {
        setToast({
          title: 'Failed to update instruction',
          severity: 'error',
        })
      }
    } else {
      // create case
      try {
        const response = await twinsNewInstruction(activeOrganization, twinId, {
          query: queryTopic,
          instructions: instructionText,
          access: instructionAccess,
          inclusions: getInclusions(),
          exclusions: getExclusions(),
        })

        onAddInstruction(response.data)

        setToast({
          title: 'Successfully added instruction',
          severity: 'success',
        })

        // if user hs not yet set twin profile, update onboarding step
        if (!hasSetTwinProfile) {
          updateOnboardingStep()
        }

        onClose()
      } catch (Error) {
        setToast({
          title: 'Failed to add instruction',
          severity: 'error',
        })
      }
    }

    setIsSubmitting(false)
  }

  const onlyInstructionIsDefined =
    isFieldEmpty(queryTopic) &&
    !isFieldEmpty(instructionText) &&
    !areAnyExclusionsSelected() &&
    !areAnyInclusionsSelected()

  const onlyInstructionAndQueryAreDefined =
    isFieldEmpty(instructionText) &&
    isFieldEmpty(queryTopic) &&
    !areAnyExclusionsSelected() &&
    !areAnyInclusionsSelected()

  const everythingIsEmpty =
    isFieldEmpty(queryTopic) &&
    isFieldEmpty(instructionText) &&
    !areAnyExclusionsSelected() &&
    !areAnyInclusionsSelected()

  const documentOrNoteIsSelected = includedDocuments.length > 0 || includedNotes.length > 0

  // validation for instruction input
  const isInstructionInputValid = () => {
    if (everythingIsEmpty) {
      return false
    } else if (onlyInstructionIsDefined) {
      return true
    } else if (onlyInstructionAndQueryAreDefined) {
      return true
    } else if (documentOrNoteIsSelected) {
      // if there is no instruction but there is at least one document or one note selected, this is valid
      return true
    }
    return true
  }

  return (
    <JelledModal
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Stack>
        <Typography mb={4} variant="h4" color="secondary">
          Add instruction
        </Typography>

        <TextFieldWithDarkMode
          className="has-label"
          isDarkBackdrop={false}
          theme={theme}
          label="Topic"
          placeholder="Instruction topic"
          value={queryTopic}
          onChange={onQueryTopicChange}
          disabled={isSubmitting}
          sx={{
            marginBottom: '48px',
            '& .MuiFormLabel-root': {
              color: getElementColor(true, theme),
              fontSize: '16px',
              fontWeight: 700,
            },
          }}
        />

        <TextFieldWithDarkMode
          isDarkBackdrop={false}
          className="has-label"
          required
          theme={theme}
          label="Instructions"
          placeholder="For topics relating to the above, refer to the the employee handbook pdf"
          value={instructionText}
          onChange={onInstructionChange}
          disabled={isSubmitting}
          error={submitCount > 0 && !isInstructionInputValid()}
          helperText={submitCount > 0 && !isInstructionInputValid() && 'You must enter an instruction'}
          sx={{
            marginBottom: '32px',
            '& .MuiFormLabel-root': {
              fontSize: '16px',
              fontWeight: 700,
            },
          }}
        />

        <Typography
          variant="h7.5"
          sx={{
            mb: '6px',
            color: COLORS.GRAY_1,
          }}>
          Apply instructions to<span style={{ color: COLORS.RED }}>*</span>
        </Typography>

        <Stack mb={1}>
          <InstructionAccessRadioGroup
            instructionAccess={instructionAccess}
            onInstructionAccessChange={onInstructionAccessChange}
            disabled={isSubmitting}
            isDarkBackdrop={false}
          />
        </Stack>

        <AttachDocumentSelector
          notes={includedNotes}
          setNotes={setIncludedNotes}
          sourceDocuments={includedDocuments}
          setSourceDocuments={setIncludedDocuments}
          documentType={attachDocumentType}
          setDocumentType={setAttachDocumentType}
        />

        <AttachedDocumentsList
          notes={includedNotes}
          setNotes={setIncludedNotes}
          sourceDocuments={includedDocuments}
          setSourceDocuments={setIncludedDocuments}
        />

        <ReferencesList references={references} setReferences={setReferences} submitCount={submitCount} />

        <Button
          color="secondary"
          endIcon={isAdvancedSettingsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          onClick={() => {
            setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)
          }}
          sx={{
            my: 2,
            width: 'fit-content',
          }}>
          Advanced settings
        </Button>

        {isAdvancedSettingsOpen && (
          <Stack
            borderRadius="20px"
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 2 : 4}
            alignItems={isMobile ? 'auto' : 'baseline'}
            p={isMobile ? '8px' : '20px'}
            sx={{
              backgroundColor: COLORS.GRAY_8,
            }}>
            <Stack width="300px">
              <Typography mb={2} variant="pXS" color="secondary">
                Inclusions
              </Typography>

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  color: COLORS.GRAY_2,
                }}>
                Notes
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllNotesIncluded}
                  disabled={areAllNotesExcluded}
                  onChange={(e) => {
                    setAreAllNotesIncluded(e.target.checked)
                    setAreSpecificNotesIncluded(false)
                    // should clear included notes because we are including all notes
                    setIncludedNotes([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificNotesIncluded}
                  onChange={(e) => {
                    setAreSpecificNotesIncluded(e.target.checked)
                    setAreAllNotesIncluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificNotesIncluded && (
                <SelectNoteButton
                  submitCount={submitCount}
                  title="Included notes"
                  notes={includedNotes}
                  setNotes={setIncludedNotes}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Documents
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllDocumentsIncluded}
                  disabled={areAllDocumentsExcluded}
                  onChange={(e) => {
                    setAreAllDocumentsIncluded(e.target.checked)
                    setAreSpecificDocumentsIncluded(false)
                    // should clear specific included documents because we are including all documents
                    setIncludedDocuments([])
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: getColorForDarkMode(theme),
                    },
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificDocumentsIncluded}
                  onChange={(e) => {
                    setAreSpecificDocumentsIncluded(e.target.checked)
                    setAreAllDocumentsIncluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificDocumentsIncluded && (
                <SelectDocumentButton
                  submitCount={submitCount}
                  title="Included documents"
                  sourceDocuments={includedDocuments}
                  setSourceDocuments={setIncludedDocuments}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Calendars
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllCalendarsIncluded}
                  disabled={areAllCalendarsExcluded}
                  onChange={(e) => {
                    setAreAllCalendarsIncluded(e.target.checked)
                    setAreSpecificCalendarsIncluded(false)
                    // should clear specific included calendars because we are including all calendars
                    setIncludedCalendars([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificCalendarsIncluded}
                  onChange={(e) => {
                    setAreSpecificCalendarsIncluded(e.target.checked)
                    setAreAllCalendarsIncluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificCalendarsIncluded && (
                <SelectCalendarButton
                  submitCount={submitCount}
                  title="Included calendars"
                  sourceCalendars={includedCalendars}
                  setSourceCalendars={setIncludedCalendars}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Email queries
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllEmailsIncluded}
                  disabled={areAllEmailsExcluded}
                  onChange={(e) => {
                    setAreAllEmailsIncluded(e.target.checked)
                    setAreSpecificEmailsIncluded(false)
                    // should clear specific included email queries because we are including all email queries
                    setIncludedEmailQueries([])
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: getColorForDarkMode(theme),
                    },
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificEmailsIncluded}
                  onChange={(e) => {
                    setAreSpecificEmailsIncluded(e.target.checked)
                    setAreAllEmailsIncluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificEmailsIncluded && (
                <SelectEmailQueryButton
                  submitCount={submitCount}
                  title="Included email queries"
                  gmailQueries={includedEmailQueries}
                  setGmailQueries={setIncludedEmailQueries}
                />
              )}
            </Stack>

            <Stack>
              <Typography mb={2} variant="pXS" color="secondary">
                Exclusions
              </Typography>

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  color: COLORS.GRAY_2,
                }}>
                Notes
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllNotesExcluded}
                  disabled={areAllNotesIncluded}
                  onChange={(e) => {
                    setAreAllNotesExcluded(e.target.checked)
                    setAreSpecificNotesExcluded(false)
                    // should clear excluded notes because we are excluding all notes
                    setExcludedNotes([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificNotesExcluded}
                  onChange={(e) => {
                    setAreSpecificNotesExcluded(e.target.checked)
                    setAreAllNotesExcluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificNotesExcluded && (
                <SelectNoteButton
                  submitCount={submitCount}
                  title="Excluded notes"
                  notes={excludedNotes}
                  setNotes={setExcludedNotes}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Documents
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllDocumentsExcluded}
                  disabled={areAllDocumentsIncluded}
                  onChange={(e) => {
                    setAreAllDocumentsExcluded(e.target.checked)
                    setAreSpecificDocumentsExcluded(false)
                    // should clear specific excluded documents because we are excluding all documents
                    setExcludedDocuments([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificDocumentsExcluded}
                  onChange={(e) => {
                    setAreSpecificDocumentsExcluded(e.target.checked)
                    setAreAllDocumentsExcluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificDocumentsExcluded && (
                <SelectDocumentButton
                  submitCount={submitCount}
                  title="Excluded documents"
                  sourceDocuments={excludedDocuments}
                  setSourceDocuments={setExcludedDocuments}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Calendars
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllCalendarsExcluded}
                  disabled={areAllCalendarsIncluded}
                  onChange={(e) => {
                    setAreAllCalendarsExcluded(e.target.checked)
                    setAreSpecificCalendarsExcluded(false)
                    // should clear specific excluded calendars because we are excluding all calendars
                    setExcludedCalendars([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificCalendarsExcluded}
                  onChange={(e) => {
                    setAreSpecificCalendarsExcluded(e.target.checked)
                    setAreAllCalendarsExcluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificCalendarsExcluded && (
                <SelectCalendarButton
                  submitCount={submitCount}
                  title="Excluded calendars"
                  sourceCalendars={excludedCalendars}
                  setSourceCalendars={setExcludedCalendars}
                />
              )}

              <Typography
                variant="pXS"
                sx={{
                  mb: 1,
                  mt: 3,
                  color: COLORS.GRAY_2,
                }}>
                Email queries
              </Typography>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areAllEmailsExcluded}
                  disabled={areAllEmailsIncluded}
                  onChange={(e) => {
                    setAreAllEmailsExcluded(e.target.checked)
                    setAreSpecificEmailsExcluded(false)
                    // should clear specific excluded email queries because we are excluding all email queries
                    setExcludedEmailQueries([])
                  }}
                />
                <Typography variant="pS" color="secondary">
                  All
                </Typography>
              </CheckboxContainerStack>

              <CheckboxContainerStack>
                <StyledCheckboxWithDarkMode
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon
                      sx={{
                        fill: COLORS.JELLED_PRIMARY_BLUE,
                      }}
                    />
                  }
                  theme={theme}
                  checked={areSpecificEmailsExcluded}
                  onChange={(e) => {
                    setAreSpecificEmailsExcluded(e.target.checked)
                    setAreAllEmailsExcluded(false)
                  }}
                />
                <Typography variant="pS" color="secondary">
                  Only specific
                </Typography>
              </CheckboxContainerStack>

              {areSpecificEmailsExcluded && (
                <SelectEmailQueryButton
                  submitCount={submitCount}
                  title="Excluded email queries"
                  gmailQueries={excludedEmailQueries}
                  setGmailQueries={setExcludedEmailQueries}
                />
              )}
            </Stack>
          </Stack>
        )}
        {submitCount > 0 && !isInstructionInputValid() && (
          <Typography variant="pXS" color="error" mt={1}>
            Please select either an inclusion or an exclusion
          </Typography>
        )}

        <ModalButtonsContainerStack>
          <Button onClick={() => onClose()} size="medium" color="primary">
            Cancel
          </Button>
          <Button variant="contained" size="medium" color="primary" onClick={onFormSubmit}>
            Save
          </Button>
        </ModalButtonsContainerStack>
      </Stack>
    </JelledModal>
  )
}

export default withOnboardingStep(AddOrEditInstructionModal, OnboardingStep.set_twin_profile)
